import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  SimpleGrid,
  Icon,
  useColorModeValue,
  Flex,
  Image,
  Badge,
  Divider,
  Stack,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { 
  FaChartLine, 
  FaUserShield, 
  FaRegHandshake, 
  FaRegClock,
  FaArrowRight,
  FaCalculator
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import { FiUsers, FiAward, FiTarget } from 'react-icons/fi';

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

const GradientText = ({ children, ...props }) => (
  <Text
    as="span"
    bgGradient="linear(to-r, brand.400, brand.600)"
    bgClip="text"
    fontWeight="bold"
    {...props}
  >
    {children}
  </Text>
);

const Feature = ({ title, text, icon, link }) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const Component = link ? RouterLink : Box;
  
  return (
    <MotionBox
      as={Component}
      to={link}
      p={8}
      bg={bgColor}
      borderRadius="2xl"
      shadow="xl"
      position="relative"
      overflow="hidden"
      _hover={{ transform: 'translateY(-5px)' }}
      transition="all 0.3s"
      whileHover={{ scale: 1.02 }}
    >
      <Box
        position="absolute"
        top={0}
        right={0}
        w="150px"
        h="150px"
        bg="brand.50"
        opacity={0.1}
        borderBottomLeftRadius="full"
      />
      <Icon as={icon} w={12} h={12} mb={6} color="brand.500" />
      <Heading size="md" mb={4} fontWeight="bold">
        {title}
      </Heading>
      <Text color={useColorModeValue('gray.600', 'gray.300')} fontSize="md">
        {text}
      </Text>
    </MotionBox>
  );
};

const StatCard = ({ number, label }) => (
  <VStack 
    spacing={2} 
    p={8} 
    bg={useColorModeValue('white', 'gray.800')} 
    borderRadius="xl"
    shadow="md"
    align="center"
  >
    <Text fontSize="4xl" fontWeight="bold" color="brand.500">
      {number}
    </Text>
    <Text color={useColorModeValue('gray.600', 'gray.300')}>
      {label}
    </Text>
  </VStack>
);

const StatBox = ({ number, label, icon: IconComponent }) => (
  <VStack spacing={2} p={8} bg={useColorModeValue('white', 'gray.800')} borderRadius="xl" shadow="md" align="center">
    <Icon as={IconComponent} w={12} h={12} mb={4} color="brand.500" />
    <Text fontSize="4xl" fontWeight="bold" color="brand.500">
      {number}
    </Text>
    <Text color={useColorModeValue('gray.600', 'gray.300')}>
      {label}
    </Text>
  </VStack>
);

const Home = () => {
  const bgColor = useColorModeValue('gray.50', 'gray.900');

  const stats = [
    { number: '1000+', label: 'Happy Clients', icon: FiUsers },
    { number: '98%', label: 'Client Satisfaction', icon: FiAward },
    { number: '25+', label: 'Years Of Market Experience', icon: FiTarget }
  ];

  return (
    <Box bg={bgColor}>
      {/* Hero Section */}
      <Box 
        position="relative" 
        py={{ base: 20, md: 32 }}
        bg={useColorModeValue('gray.50', 'gray.900')}
      >
        <Container maxW="container.xl">
          <Stack 
            direction={{ base: 'column', lg: 'row' }} 
            spacing={{ base: 10, lg: 20 }}
            align="center"
          >
            <MotionFlex
              flex={1}
              direction="column"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Badge 
                colorScheme="brand" 
                fontSize="sm" 
                mb={6}
                borderRadius="full" 
                px={4} 
                py={1}
                width="fit-content"
              >
                Welcome to Budget Brilliance
              </Badge>
              <Heading 
                as="h1" 
                size="3xl" 
                mb={6} 
                lineHeight="shorter"
                fontWeight="extrabold"
              >
                Empower Your <GradientText>Financial Future</GradientText> With Expert Guidance
              </Heading>
              <Text 
                fontSize="xl" 
                mb={8} 
                color={useColorModeValue('gray.600', 'gray.300')}
                lineHeight="tall"
              >
                Join our elite network of mutual fund distributors and help shape the financial success stories of tomorrow.
              </Text>
              <HStack spacing={4}>
                <Button
                  as={RouterLink}
                  to="/register"
                  size="lg"
                  colorScheme="brand"
                  px={8}
                  height="60px"
                  fontSize="lg"
                  rightIcon={<FaArrowRight />}
                  _hover={{
                    transform: 'translateY(-2px)',
                    boxShadow: 'xl',
                  }}
                >
                  Start Your Journey
                </Button>
                <Button
                  as={RouterLink}
                  to="/login"
                  size="lg"
                  variant="outline"
                  colorScheme="brand"
                  px={8}
                  height="60px"
                  fontSize="lg"
                  _hover={{
                    bg: 'brand.50',
                  }}
                >
                  Sign In
                </Button>
              </HStack>
            </MotionFlex>
            <MotionBox
              flex={1}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Box
                position="relative"
                _after={{
                  content: '""',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '500px',
                  height: '500px',
                  bg: 'brand.500',
                  opacity: 0.1,
                  borderRadius: 'full',
                  zIndex: 0,
                }}
              >
                <Image 
                  src="/images/hero-finance.svg" 
                  alt="Financial Growth Illustration"
                  maxH="500px"
                  objectFit="contain"
                  position="relative"
                  zIndex={1}
                />
              </Box>
            </MotionBox>
          </Stack>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box py={16} bg={useColorModeValue('white', 'gray.800')}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
            {stats.map((stat, index) => (
              <StatBox key={index} {...stat} />
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box py={20}>
        <Container maxW="container.xl">
          <VStack spacing={16}>
            <VStack spacing={4} textAlign="center" maxW="container.md">
              <Heading size="2xl" fontWeight="extrabold">
                Why Choose <GradientText>Budget Brilliance</GradientText>
              </Heading>
              <Text fontSize="xl" color={useColorModeValue('gray.600', 'gray.300')}>
                Experience a suite of powerful tools designed to elevate your mutual fund distribution business
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8} w="full">
              <Feature
                icon={FaCalculator}
                title="Investment Calculator"
                text="Plan your financial future with our comprehensive investment calculator."
                link="/calculator"
              />
              <Feature
                icon={FaChartLine}
                title="Real-Time Analytics"
                text="Track your performance with advanced analytics and insights to make data-driven decisions."
              />
              <Feature
                icon={FaUserShield}
                title="Bank-Grade Security"
                text="Rest easy knowing your data is protected with enterprise-level security protocols."
              />
              <Feature
                icon={FaRegHandshake}
                title="Smart CRM"
                text="Manage client relationships effortlessly with our intuitive CRM system."
              />
              <Feature
                icon={FaRegClock}
                title="Instant Onboarding"
                text="Get started in minutes with our streamlined digital onboarding process."
              />
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box 
        py={20} 
        bg={useColorModeValue('brand.50', 'brand.900')}
        position="relative"
        overflow="hidden"
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={8} textAlign="center">
            <Heading size="2xl" fontWeight="extrabold">
              Ready to Transform Your Business?
            </Heading>
            <Text fontSize="xl" maxW="container.md" color={useColorModeValue('gray.600', 'gray.300')}>
              Join thousands of successful mutual fund distributors who have already taken their business to the next level.
            </Text>
            <Button
              as={RouterLink}
              to="/register"
              size="lg"
              colorScheme="brand"
              px={12}
              py={8}
              fontSize="xl"
              rightIcon={<FaArrowRight />}
              _hover={{
                transform: 'translateY(-2px)',
                boxShadow: 'xl',
              }}
            >
              Get Started Now
            </Button>
          </VStack>
        </Container>
        {/* Decorative Elements */}
        <Box
          position="absolute"
          top="20%"
          left="-5%"
          w="200px"
          h="200px"
          bg="brand.500"
          opacity={0.1}
          borderRadius="full"
        />
        <Box
          position="absolute"
          bottom="10%"
          right="-5%"
          w="300px"
          h="300px"
          bg="brand.500"
          opacity={0.1}
          borderRadius="full"
        />
      </Box>
    </Box>
  );
};

export default Home;
